import { Link, router } from "@inertiajs/react";
import axios from "axios";
import { v4 as uuidv4 } from 'uuid';

import { css, styled } from "@/root/stitches.config";

import useAuth from "@/hooks/useAuth";
import useNavbar from "@/hooks/useNavbar";

import { Button } from "@/components/button";
import { Grid } from "@/components/grid";
import { Divider } from "@/components/divider";
import { Title } from "@/components/title";
import { Text } from "@/components/text";

import ComingSoon from "@/assets/misc/coming-soon.webp";
import Frame from "@/assets/activities/frame.webp";
import TagBlue from "@/assets/activities/tag-blue.svg";
import TagOrange from "@/assets/activities/tag-orange.svg";
import { useCallback, useState, useLayoutEffect } from "react";

const Container = styled("section", {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    gap: "2.5rem",
    padding: "3rem 5%",
    backgroundColor: "$white",
    zIndex: 2,
    translate: "0 -10px"
});

const Activity = styled("article", {
    display: "flex",
    flexDirection: "column",
    gap: "1.5rem",
});

const DisabledBtn = styled("div", {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    height: "$button-desktop-height",
    border: "$dark 0.3vw solid",
    borderRadius: "0.5rem",
    "@laptop": {
        height: "$button-laptop-height",
    },
    "@tablet": {
        height: "$button-tablet-height",
    },
    "@mobile": {
        height: "$button-mobile-height",
    },
});

const ActivityImage = styled("span", {
    display: "flex",
    position: "relative",
    zIndex: "1",
    alignItems: "center",
    justifyContent: "center",
    width: "auto",
    height: "18.2292vw",
    backgroundPosition: "center",
    backgroundSize: "contain",
    backgroundRepeat: "no-repeat",
    variants: {
        frame: {
            activity: {
                backgroundImage: "none",
            },
            competition: {
                backgroundImage: `url("${Frame}")`,
            },
        },
    },
    "@tablet": {
        height: "35vw",
    },
    "@mobile": {
        height: "72vw"
    }
});

const ActivityBody = styled("div", {
    display: "flex",
    flexDirection: "column",
    gap: "0.5rem",
    padding: "0",
    "@tablet": { padding: "0 1rem" },
    "@mobile": { padding: "0 1rem" },
});

const ActivityTag = styled("div", {
    display: "flex",
    gap: "0.5rem",
    variants: {
        tag: {
            activity: { color: "$tertiary" },
            competition: { color: "$secondary" },
        },
    },
});

export default function Activities({ activities, competitions }) {
    const [acts] = useState([...activities, ...competitions]);
    const [filteredActs, setFilteredActs] = useState(acts);

    const { isAuthenticated } = useAuth();
    const {
        links: { authUrl },
    } = useNavbar();

    const handleFilterActs = useCallback((filterBy) => {
        setFilteredActs(
            filteredActs.filter(function (act) {
                if (filterBy === "all") return true;
                return act.type.toLowerCase() === filterBy;
            })
        );
    }, []);

    async function handleRedirectToOrderPage(orderUrl) {
        if (isAuthenticated) {
            return router.visit(orderUrl);
        }

        const response = await axios.get(authUrl.attempt);

        if (response.status === 200) {
            return window.location.replace(response.data.meta.redirectUrl);
        }
    }

    return (
        <Container>
            <header
                style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "1rem",
                }}
            >
                <Title
                    css={{ textAlign: "center", fontSize: "3.5vw", "@mobile": { fontSize: "7.8vw" } }}
                // color="light"
                >
                    Activities
                </Title>
                <Divider color="dark" css={{ marginBottom: "1rem" }} />
                <div
                    className={css({
                        display: "flex",
                        justifyContent: "center",
                        gap: "1rem",
                        width: "100%",
                        "@mobile": { flexDirection: "column" },
                    }).toString()}
                >
                    <Button
                        // color="light"
                        onClick={() => handleFilterActs("all")}
                        fullWidth
                    >
                        All
                    </Button>
                    <Button
                        // color="light"
                        onClick={() => handleFilterActs("activity")}
                        fullWidth
                    >
                        Activities
                    </Button>
                    <Button
                        // color="light"
                        onClick={() => handleFilterActs("competition")}
                        fullWidth
                    >
                        Competitions
                    </Button>
                </div>
            </header>
            <Grid
                cols={3}
                css={{
                    color: "$white",
                    borderTop: "0.2vw solid $white",
                    padding: "2rem 0",
                }}
            >
                {filteredActs.map((activity) => {
                    console.log("DEBUG ACTIVITY:", activity);
                    const isActivity = activity.type.toLowerCase() === "activity";
                    const uniqueKey = uuidv4();
                    return (
                        <Link
                            key={uniqueKey}
                            href={activity.details_url}
                            style={{ textDecoration: "none" }}
                        >
                            <Activity>
                                <div className={css({ position: "relative" }).toString()}>
                                    <ActivityImage frame={activity.type} />
                                    <div
                                        className={css({
                                            position: "absolute",
                                            top: "57%",
                                            left: "50%",
                                            translate: "-50% -50%",
                                            display: "flex",
                                            justifyContent: "center",
                                            alignItems: "center",
                                            width: "100%",
                                            "@mobile": {
                                                paddingLeft: activity.type.toLowerCase() === "activity" ? "0" : "80px",
                                                paddingRight: activity.type.toLowerCase() === "activity" ? "0" : "80px"
                                            }
                                        }).toString()}
                                    >
                                        {activity.image_url ? (
                                            <img
                                                className={css({
                                                    width: activity.type.toLowerCase() === "activity" ? "88%" : "44%",
                                                    height: activity.type.toLowerCase() === "activity" ? "134%" : "67%",
                                                    objectFit: "contain",
                                                    "@tablet": {
                                                        width: activity.type.toLowerCase() === "activity" ? "70%" : "60%"
                                                    },
                                                    "@mobile": {
                                                        width: "100%",
                                                        objectPosition: activity.type.toLowerCase() === "activity" ? "bottom" : "center",
                                                     }
                                                }).toString()}
                                                src={activity.image_url}
                                                alt="Failed to load"
                                            />
                                        ) : (
                                            <img
                                                className={css({
                                                    width: activity.type.toLowerCase() === "activity" ? "48%" : "40%",
                                                    height: activity.type.toLowerCase() === "activity" ? "60%" : "50%",
                                                    objectFit: "contain",
                                                    "@tablet": {
                                                        width: activity.type.toLowerCase() === "activity" ? "68%" : "55%"
                                                    },
                                                    "@mobile": {
                                                        width: "100%",
                                                    }
                                                }).toString()}
                                                src={ComingSoon}
                                                alt="Coming soon"
                                            />
                                        )}

                                    </div>
                                </div>

                                <ActivityBody>
                                    <div
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                        }}
                                    >
                                        <Text
                                            css={{
                                                color: "$dark",
                                                wordBreak: "keep-all",
                                                overflow: "hidden",
                                                width: "70%",
                                                padding: "0.1vw 0",
                                                fontSize: activity.type.toLowerCase() === "activity" || activity.type.toLowerCase() === "competition"
                                                    ? "1rem"
                                                    : "1.2vw",
                                                "@mobile": {
                                                    width: "60%",
                                                    fontSize: activity.type.toLowerCase() === "activity" || activity.type.toLowerCase() === "competition"
                                                        ? "1rem"
                                                        : "1.2rem"
                                                },
                                                "@tablet": {
                                                    fontSize: activity.type.toLowerCase() === "activity" || activity.type.toLowerCase() === "competition"
                                                        ? "1.2rem"
                                                        : "2vw"
                                                }
                                            }}
                                        >
                                            {activity.name}
                                        </Text>
                                        <ActivityTag tag={activity.type} css={{ alignItems: "center" }}>
                                            {isActivity ? (
                                                <img
                                                src={TagBlue}
                                                alt="Activity"
                                                style={{ width: "12.5px", height: "12.5px" }}
                                                />
                                            ) : (
                                                <img
                                                    src={TagOrange}
                                                    alt="Competition"
                                                    style={{ width: "12.5px", height: "12.5px" }}
                                                />
                                            )}
                                            <Text
                                                css={{
                                                    fontSize: "1rem",
                                                    color: isActivity
                                                        ? "$dark"
                                                        : "$secondary"
                                                }}
                                            >
                                                {activity.type}
                                            </Text>
                                        </ActivityTag>
                                    </div>
                                    <Text
                                        css={{
                                            color: "$dark",
                                        }}
                                        className={css({ wordBreak: "keep-all", overflow: "hidden" }).toString()}
                                    >
                                        {!isActivity
                                            ? "Rp " +activity.price.toLocaleString("id-ID")
                                            : (isActivity && (activity.sale.name))}
                                    </Text>
                                </ActivityBody>
                                {(isActivity && activity.is_coming_up) || (!isActivity && activity.is_opened) ? (
                                    <DisabledBtn css={{ borderColor: "$dark" }}>
                                        <Text css={{ color: "$dark" }}>
                                            Coming Soon
                                        </Text>
                                    </DisabledBtn>
                                ) : (isActivity && !activity.sale.is_tickets_available) || (isActivity && !activity.is_going_on) ? (
                                    <DisabledBtn>
                                        <Text css={{ color: "$dark" }}>
                                            {activity.is_going_on ? "Sold Out" : "Order Closed"}
                                        </Text>
                                    </DisabledBtn>
                                ) : (!isActivity && activity.is_closed) || (!isActivity && activity.is_quota_full) ? (
                                    <DisabledBtn>
                                        <Text css={{ color: "$dark" }}>
                                            Registration Closed
                                        </Text>
                                    </DisabledBtn>
                                ) : (!isActivity && activity.price_tag == "OTS") ? (
                                    <DisabledBtn css={{ borderColor: "$dark" }}>
                                        <Text css={{ color: "$dark" }}>
                                            Offline Registration
                                        </Text>
                                    </DisabledBtn>
                                ) : (
                                    <div
                                        onClick={(evt) => {
                                            evt.preventDefault();
                                            handleRedirectToOrderPage(
                                                activity.order_url
                                            );
                                            useLayoutEffect(() => {
                                                window.scrollTo(0, 0)
                                            });
                                        }}
                                    >
                                        <Button
                                            fullWidth>
                                            {isActivity
                                                ? "Order Now"
                                                : "Register Now"}
                                        </Button>
                                    </div>
                                )}
                            </Activity>
                        </Link>
                    );
                })}
            </Grid>
        </Container>
    );
}
